/**
 * Fetches channel data from famelack/famelack-data (tv/compressed).
 *
 * Stability model (manifest sources tried in order):
 *   1. Self-hosted manifest at `MANIFEST_URL` (default: https://asva.tv/channels-manifest.json).
 *      Fastest, no third-party dependency, no rate limits, fully under your control.
 *   2. Upstream raw manifest at `${RAW_BASE}/manifest.json` (if famelack-data ever adds one).
 *   3. api.github.com directory listing — last resort; capped at ~60 req/hr per IP.
 *
 *   All network calls use a bounded timeout + bounded exponential-backoff retry
 *   so transient network hiccups don't surface as hard errors. A single bad
 *   country/category file is skipped, not fatal.
 *
 * Optional env pins (build time):
 *   REACT_APP_CHANNELS_REF       — git ref/SHA to pull data from. Default: `main`.
 *   REACT_APP_CHANNELS_MANIFEST  — override the self-hosted manifest URL. Set to
 *                                  empty string to skip the self-hosted step and
 *                                  go straight to GitHub.
 */

const CHANNELS_REF =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_CHANNELS_REF) || 'main';

const MANIFEST_URL_DEFAULT = 'https://asva.tv/channels-manifest.json';
const MANIFEST_URL_OVERRIDE =
  typeof process !== 'undefined' && process.env && process.env.REACT_APP_CHANNELS_MANIFEST;
const MANIFEST_URL =
  MANIFEST_URL_OVERRIDE === undefined ? MANIFEST_URL_DEFAULT : MANIFEST_URL_OVERRIDE;

const RAW_BASE = `https://raw.githubusercontent.com/famelack/famelack-data/${CHANNELS_REF}/tv/compressed`;
const GITHUB_API_BASE = 'https://api.github.com/repos/famelack/famelack-data';
const COMPRESSED_API_PATH = `${GITHUB_API_BASE}/contents/tv/compressed?ref=${encodeURIComponent(CHANNELS_REF)}`;

const REQUEST_TIMEOUT_MS = 15_000;
const MAX_RETRIES = 3;
const BASE_BACKOFF_MS = 400;
const FETCH_CONCURRENCY = 10;

/**
 * Normalize channel objects from famelack compressed JSON for the app.
 * - `stream_urls` → `iptv_urls`
 * - `languages: string[]` (upstream) → `language` primary for legacy UI/filters
 */
export function normalizeChannelList(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((ch) => {
    const out = { ...ch };
    if (out.stream_urls != null && out.iptv_urls == null) {
      out.iptv_urls = Array.isArray(out.stream_urls) ? out.stream_urls : [];
    }
    if (out.youtube_urls == null) out.youtube_urls = [];
    if (out.language == null && Array.isArray(out.languages) && out.languages.length > 0) {
      out.language = out.languages[0];
    }
    return out;
  });
}

function isGzip(buf) {
  return buf.length >= 2 && buf[0] === 0x1f && buf[1] === 0x8b;
}

async function decompressGzip(buf) {
  const ds = new DecompressionStream('gzip');
  const stream = new Blob([buf]).stream().pipeThrough(ds);
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Fetch with timeout + retry. Throws only after MAX_RETRIES attempts. */
async function fetchWithRetry(url, { retries = MAX_RETRIES, timeoutMs = REQUEST_TIMEOUT_MS } = {}) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const r = await fetch(url, { signal: controller.signal });
      if (!r.ok) {
        /* Retry on 5xx and 429 only; 4xx (other) is almost certainly terminal. */
        if (r.status >= 500 || r.status === 429) {
          throw new Error(`HTTP ${r.status}: ${url}`);
        }
        throw Object.assign(new Error(`HTTP ${r.status}: ${url}`), { terminal: true });
      }
      return r;
    } catch (err) {
      lastErr = err;
      if (err && err.terminal) break;
      if (attempt === retries) break;
      const delay = BASE_BACKOFF_MS * Math.pow(2, attempt) + Math.floor(Math.random() * 200);
      await sleep(delay);
    } finally {
      clearTimeout(timer);
    }
  }
  throw lastErr || new Error(`fetch failed: ${url}`);
}

async function fetchJson(url, opts) {
  const r = await fetchWithRetry(url, opts);
  const buf = new Uint8Array(await r.arrayBuffer());
  const jsonBytes = isGzip(buf) ? await decompressGzip(buf) : buf;
  return JSON.parse(new TextDecoder('utf-8').decode(jsonBytes));
}

/** Run async tasks with a fixed concurrency cap. */
async function runWithConcurrency(items, worker, concurrency = FETCH_CONCURRENCY) {
  const results = new Array(items.length);
  let cursor = 0;
  const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (true) {
      const i = cursor++;
      if (i >= items.length) return;
      results[i] = await worker(items[i], i);
    }
  });
  await Promise.all(runners);
  return results;
}

/**
 * Try to load a static manifest that enumerates all country/category files.
 * Shape: { countries: string[], categories: string[], metadata?: string, version?: string }
 * Codes/names MUST be the filename stems (e.g. "us", "news") without ".json".
 *
 * Attempts, in order:
 *   1. Self-hosted: MANIFEST_URL (asva.tv by default)
 *   2. Upstream raw: ${RAW_BASE}/manifest.json
 */
function isValidManifest(m) {
  return m && Array.isArray(m.countries) && Array.isArray(m.categories);
}

async function tryFetchManifest() {
  const sources = [];
  if (MANIFEST_URL) sources.push({ url: MANIFEST_URL, label: 'self-hosted' });
  sources.push({ url: `${RAW_BASE}/manifest.json`, label: 'github-raw' });

  for (const { url, label } of sources) {
    try {
      const manifest = await fetchJson(url, { retries: 1, timeoutMs: 8_000 });
      if (isValidManifest(manifest)) {
        if (typeof console !== 'undefined') {
          console.debug(`[channels] using ${label} manifest:`, url);
        }
        return manifest;
      }
    } catch (_) {
      /* try the next source */
    }
  }
  return null;
}

/** Legacy path: ask api.github.com for the file list under a directory. */
async function listDirViaApi(dirName) {
  const contents = await fetchJson(COMPRESSED_API_PATH, { retries: 2 });
  const dir = contents.find((c) => c.type === 'dir' && c.name === dirName);
  if (!dir || !dir.git_url) throw new Error(`${dirName} dir not found`);
  const tree = await fetchJson(dir.git_url, { retries: 2 });
  return (tree.tree || [])
    .filter((n) => n.path && n.path.endsWith('.json'))
    .map((n) => n.path.replace(/\.json$/, ''));
}

async function fetchGroup({ dirName, stems }) {
  const out = {};
  await runWithConcurrency(stems, async (stem) => {
    const url = `${RAW_BASE}/${dirName}/${stem}.json`;
    try {
      const data = await fetchJson(url);
      out[stem] = normalizeChannelList(Array.isArray(data) ? data : []);
    } catch (err) {
      /* Skip a single broken file rather than failing the whole load. */
      if (typeof console !== 'undefined') {
        console.warn(`[channels] skipping ${dirName}/${stem}.json:`, err && err.message);
      }
    }
  });
  return out;
}

async function fetchCountriesMetadata(filename = 'countries_metadata.json') {
  return fetchJson(`${RAW_BASE}/${filename}`, { retries: 2 });
}

/**
 * Fetch all channel data from GitHub (compressed).
 * @returns {Promise<{ countries: Object, categories: Object, countriesMetadata: Object }>}
 */
export async function fetchChannelsFromGitHub() {
  const manifest = await tryFetchManifest();

  const [countryStems, categoryStems] = manifest
    ? [manifest.countries, manifest.categories]
    : await Promise.all([listDirViaApi('countries'), listDirViaApi('categories')]);

  const metadataFile = manifest?.metadata || 'countries_metadata.json';

  const [countries, categories, countriesMetadata] = await Promise.all([
    fetchGroup({ dirName: 'countries', stems: countryStems }),
    fetchGroup({ dirName: 'categories', stems: categoryStems }),
    fetchCountriesMetadata(metadataFile),
  ]);

  return { countries, categories, countriesMetadata };
}

export default fetchChannelsFromGitHub;
